import React, { useCallback } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { Form, FormField } from 'components/ui/form';
import { InputGs, SearchableSelectGs } from 'components/ui/Forms';
import { Button } from 'components/ui/ButtonGs';
import { useMutation, useQueryClient, useQuery } from 'react-query';
import { useErrorHandler } from 'exceptions/ErrorHandler';
import { formSchema } from '../../formSchema';
import { useValePedagio } from 'hooks/api/options';
import { useHubVehicle, useHubCarrier } from 'hooks/api';
import { useHistory } from 'react-router-dom';
import { toast } from 'sonner';

export const CreateForm: React.FC = () => {
  const navigate = useHistory();
  const queryClient = useQueryClient();
  const { errorHandler } = useErrorHandler();
  const {
    createVehicle,
    listVehicleType,
    listVehicleBrand,
    listVehicleModel,
    listVehicleSubtype,
  } = useHubVehicle();

  const { fetchCarriers } = useHubCarrier();
  const { getModoCompra } = useValePedagio();
  const [selectedBrand, setSelectedBrand] = React.useState<string>('');
  const [selectedVehicleType, setSelectedVehicleType] = React.useState<
    string | null
  >(null);

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
  });

  const mutation = useMutation(createVehicle, {
    onSuccess: () => {
      queryClient.invalidateQueries('createVehicle');
      navigate.push('/settings/hub-Vehicles');
      toast.success('Veiculo criada com sucesso!');
    },
    onError: (error) => {
      errorHandler(error);
    },
  });

  const { data: dataVehicleTypes, isLoading: isLoadingVehicleTypes } = useQuery(
    {
      queryKey: ['listVehicleType'],
      queryFn: () => listVehicleType({}),
      refetchOnWindowFocus: false,
    }
  );
  const { data: dataVehicleSubtypes, isLoading: isLoadingVehicleSubtypes } =
    useQuery({
      queryKey: ['listVehicleSubtype', selectedVehicleType],
      queryFn: () =>
        selectedVehicleType
          ? listVehicleSubtype({ vehicle_type_id: selectedVehicleType })
          : Promise.resolve([]),
      enabled: !!selectedVehicleType,
      refetchOnWindowFocus: false,
    });

  //   const { data: dataVehicleBrand, isLoading: isLoadingVehicleBrand } = useQuery(
  //     {
  //       queryKey: ["listVehicleBrand"],
  //       queryFn: () => listVehicleBrand({}),
  //       refetchOnWindowFocus: false,
  //     }
  //   );

  //   const { data: dataModel, isLoading: isLoadingModel } = useQuery({
  //     queryKey: ["listVehicleModel", selectedBrand],
  //     queryFn: () => listVehicleModel({ vehicle_brand_id: selectedBrand }),
  //     refetchOnWindowFocus: false,
  //   });

  const { data: carriers, isLoading: loadinDocTypes } = useQuery({
    queryKey: ['fetchCarriers'],
    queryFn: () => fetchCarriers(),
    staleTime: 1000 * 60 * 5, // 5 min,
    refetchOnWindowFocus: false,
  });
  const { data: listModoCompra, isLoading: loadingListModoCompra } = useQuery({
    queryKey: ['getModoCompra'],
    queryFn: () => getModoCompra({}),
    staleTime: 1000 * 60 * 5, // 5 min,
    refetchOnWindowFocus: false,
  });

  //   const handleBrandChange = useCallback(
  //     (brand_id: string) => {
  //       form.setValue("vehicle_model_id", "");
  //       setSelectedBrand(() => brand_id);
  //     },
  //     [form]
  //   );

  const handleTypeVehicleChange = useCallback(
    (vehicle_type_id: string) => {
      form.setValue('vehicle_subtype_id', '');
      if (vehicle_type_id === '1') {
        form.setValue('tag_id', '', { shouldValidate: true });
        form.clearErrors('tag_id');
      } else {
        form.trigger('tag_id');
      }
      setSelectedVehicleType(() => vehicle_type_id);
    },
    [form]
  );

  const onSubmit = (data: z.infer<typeof formSchema>) => {
    mutation.mutate({
      ...data,
      vehicle_type_id: Number(data.vehicle_type_id),
      //   vehicle_brand_id: Number(data.vehicle_brand_id),
      //   vehicle_model_id: Number(data.vehicle_model_id),
      total_eixos: Number(data.qtd_eixos) + Number(data.qtd_eixos_suspenso),
	  tag_id: !data.tag_id ? null : data.tag_id
    });
  };

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="flex flex-col">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <FormField
            name="carrier_id"
            control={form.control}
            render={({ field }) => (
              <SearchableSelectGs
                {...field}
                label={'Transportadora'}
                onValueChange={(value: string) => {
                  field.onChange(value);
                }}
                content={carriers?.comboOptions || []}
                isLoading={loadinDocTypes}
                value={field.value ?? undefined}
              />
            )}
          />
          <FormField
            name="vehicle_type_id"
            control={form.control}
            render={({ field }) => (
              <SearchableSelectGs
                {...field}
                label={'Tipo de Veiculo'}
                onValueChange={(value: string) => {
                  field.onChange(value);
                  handleTypeVehicleChange(value);
                }}
                content={dataVehicleTypes || []}
                isLoading={isLoadingVehicleTypes}
                value={field.value ?? undefined}
              />
            )}
          />
          <FormField
            name="vehicle_subtype_id"
            control={form.control}
            render={({ field }) => (
              <SearchableSelectGs
                {...field}
                label={'Subtipo'}
                onValueChange={(value: string) => {
                  field.onChange(value);
                }}
                content={dataVehicleSubtypes || []}
                isLoading={isLoadingVehicleSubtypes}
                value={field.value ?? undefined}
                disabled={!selectedVehicleType}
              />
            )}
          />
          <FormField
            name="vehicle_plate"
            control={form.control}
            render={({ field }) => (
              <InputGs {...field} label="Placa do Veiculo" />
            )}
          />
          <FormField
            name="qtd_eixos"
            control={form.control}
            render={({ field }) => (
              <InputGs {...field} type="number" label="Qtd.Eixos" min={0} />
            )}
          />
          <FormField
            name="qtd_eixos_suspenso"
            control={form.control}
            render={({ field }) => (
              <InputGs
                {...field}
                type="number"
                label="Qtd.Eixos Suspensos"
                min={0}
              />
            )}
          />
          <FormField
            name="tag_id"
            control={form.control}
            render={({ field }) => (
              <SearchableSelectGs
                {...field}
                label={'Tag'}
                onValueChange={(value: string) => {
                  field.onChange(value);
                }}
                content={listModoCompra || []}
                isLoading={loadingListModoCompra}
                value={field.value ?? undefined}
              />
            )}
          />

          {/* <FormField
            name="renavam"
            control={form.control}
            render={({ field }) => <InputGs {...field} label="Renavam" />}
          /> 

         <FormField
            name="vehicle_brand_id"
            control={form.control}
            render={({ field }) => (
              <SearchableSelectGs
                {...field}
                label={"Marca"}
                onValueChange={(value: string) => {
                  field.onChange(value);
                  handleBrandChange(value);
                }}
                content={dataVehicleBrand || []}
                isLoading={isLoadingVehicleBrand}
                value={field.value ?? undefined}
              />
            )}
          />

          <FormField
            name="manufacture_year"
            control={form.control}
            render={({ field }) => (
              <InputGs {...field} label="Ano de fabricação" />
            )}
          />

          <FormField
            name="vehicle_model_id"
            control={form.control}
            render={({ field }) => (
              <SearchableSelectGs
                {...field}
                label={"Modelo"}
                onValueChange={(value: string) => {
                  field.onChange(value);
                }}
                content={dataModel || []}
                isLoading={isLoadingModel}
                value={field.value ?? undefined}
                disabled={!selectedBrand}
              />
            )}
          /> 

           <FormField
            name="color"
            control={form.control}
            render={({ field }) => (
              <InputGs {...field} label="Cor do Veiculo" />
            )}
          /> */}
        </div>
        <div className="flex justify-end w-full gap-4">
          <Button.Root
            variant="secondary"
            size="sm"
            type="button"
            onClick={() => navigate.push('/settings/hub-Vehicles')}
          >
            Cancelar
          </Button.Root>
          <Button.Root
            variant="primary"
            size="sm"
            type="submit"
            disabled={mutation.isLoading}
          >
            Salvar
          </Button.Root>
        </div>
      </form>
    </Form>
  );
};
